/** @jsxImportSource @emotion/react */
import {
  Button,
  ButtonProps,
  createStyles,
  IconName,
  useTheme,
} from '@quickbit/qb-design-system';
import { ReactNode, useState, TouchEvent, useRef, useEffect } from 'react';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { useMedia } from 'react-use';
import { Header } from 'components';
import {
  useNavigateWithSnapshots,
  useSnapshots,
  type NavigationPath,
  useSetElementToSnapshot,
} from 'context';
import { useAndroidBackButton, usePlatform } from 'hooks';
import AnimatedPage from './AnimatedPage';

interface BaseLayoutProps {
  children: ReactNode;
  title?: string;
  isRootPage?: boolean;
  breadcrumb?: string;
  actions?: {
    icon: IconName;
    onClick: () => void;
  }[];
  progress?: number;
  primaryButtonProps?: Omit<ButtonProps, 'variant'>;
  secondaryButtonProps?: Omit<ButtonProps, 'variant'>;
  textButtonProps?: Omit<ButtonProps, 'variant'>;
  onRefresh?: () => Promise<unknown>;
}

interface LayoutPropsWithoutBack extends BaseLayoutProps {
  backNavigationPath?: never;
  onBackSideEffect?: never;
}

interface LayoutPropsWithBack extends BaseLayoutProps {
  backNavigationPath: NavigationPath;
  onBackSideEffect?: () => void;
}

export type LayoutProps = LayoutPropsWithBack | LayoutPropsWithoutBack;

const MIN_DRAG_DELTA_TO_BACK = 10;

export const Layout = ({
  children,
  title,
  breadcrumb,
  progress,
  primaryButtonProps,
  secondaryButtonProps,
  textButtonProps,
  backNavigationPath,
  onBackSideEffect,
  isRootPage = false,
  onRefresh,
}: LayoutProps) => {
  const { spacing } = useTheme();
  const styles = useStyles();
  const ref = useRef<HTMLDivElement | null>(null);
  const animatedPageRef = useRef<HTMLDivElement | null>(null);
  useSetElementToSnapshot(animatedPageRef);
  const { snapshots, navDirection, previousLocation } = useSnapshots();
  const [isInModal, setIsInModal] = useState(false);
  const { isAndroid, isNative } = usePlatform();
  const navigate = useNavigateWithSnapshots();
  const { breakpoints } = useTheme();
  const isDesktop = useMedia(breakpoints.laptopSmall.replace('@media ', '')); // TODO: put in DS
  const shouldRenderAnimated = !isRootPage && (isNative || !isDesktop);
  const previousPageRoute =
    typeof backNavigationPath === 'string'
      ? backNavigationPath
      : backNavigationPath == -1
      ? previousLocation
      : undefined;
  useAndroidBackButton(
    previousPageRoute ? previousPageRoute : backNavigationPath
  );

  const dragStart = useRef<number | null>(null);
  const dragLast = useRef<number | null>(null);
  const [dragNow, setDragNow] = useState(0);

  useEffect(() => {
    if (ref.current) {
      if (ref.current.parentElement) {
        if ('id' in ref.current.parentElement) {
          setIsInModal(ref.current.parentElement.id == 'MODAL');
        }
      }
    }
  }, [ref]);

  const handleDrag = (e: TouchEvent<HTMLDivElement>) => {
    if (isAndroid) {
      // Android has hardware back button
      return;
    }
    e.stopPropagation();

    if (e.touches.length > 0 && dragStart.current !== null) {
      const diff = e.touches[0].clientX - dragStart.current;
      dragLast.current = dragNow;
      setDragNow(Math.max(0, diff));
    }
  };

  const handleDragEnd = () => {
    if (dragNow && dragLast.current !== null && backNavigationPath) {
      const deltaDrag = dragNow - dragLast.current;
      if (deltaDrag >= MIN_DRAG_DELTA_TO_BACK) {
        onBackSideEffect?.();
        navigate(backNavigationPath, 'none');
      } else if (dragNow > window.innerWidth / 2) {
        onBackSideEffect?.();
        navigate(backNavigationPath, 'none');
      }
    }
    dragStart.current = null;
    setDragNow(0);
  };

  const handleDragStart = (e: TouchEvent<HTMLDivElement>) => {
    if (e.touches.length > 0) {
      dragStart.current = e.touches[0].clientX;
    }
  };

  const content = (
    <>
      {title && (
        <Header
          title={title}
          onBack={
            backNavigationPath
              ? () => {
                  onBackSideEffect?.();
                  navigate(backNavigationPath, 'backward');
                }
              : undefined
          }
          breadcrumb={breadcrumb}
          progress={progress}
          isInModal={isInModal}
        />
      )}
      {onRefresh ? (
        <PullToRefresh onRefresh={onRefresh} pullingContent={<></>}>
          <main css={[styles.container, !isInModal && styles.largeContainer]}>
            {children}
          </main>
        </PullToRefresh>
      ) : (
        <main css={[styles.container, !isInModal && styles.largeContainer]}>
          {children}
        </main>
      )}
      {(primaryButtonProps || secondaryButtonProps || textButtonProps) && (
        <section css={styles.footer}>
          <div css={styles.buttonContainer}>
            {primaryButtonProps && (
              <Button
                variant="primary"
                text={primaryButtonProps.text}
                icon={primaryButtonProps.icon}
                loading={primaryButtonProps.loading}
                disabled={primaryButtonProps.disabled}
                onClick={primaryButtonProps.onClick}
                testId="primary-button"
              />
            )}
            {secondaryButtonProps && (
              <>
                <div css={styles.buttonSeparator} />
                <Button
                  variant="secondary"
                  text={secondaryButtonProps.text}
                  icon={secondaryButtonProps.icon}
                  loading={secondaryButtonProps.loading}
                  disabled={secondaryButtonProps.disabled}
                  onClick={secondaryButtonProps.onClick}
                  testId="secondary-button"
                />
              </>
            )}
            {textButtonProps && (
              <>
                <div css={styles.buttonSeparator} />
                <Button
                  variant="text"
                  text={textButtonProps.text}
                  icon={textButtonProps.icon}
                  loading={textButtonProps.loading}
                  disabled={textButtonProps.disabled}
                  onClick={textButtonProps.onClick}
                  testId="text-button"
                />
              </>
            )}
          </div>
        </section>
      )}
    </>
  );

  return (
    <div
      data-testid="layout-wrapper"
      id="layout-wrapper"
      ref={ref}
      css={[
        styles.wrapper,
        !isInModal && styles.largeWrapper,
        { transform: `translateX(${dragNow}px)` },
        dragNow === 0 && { transition: 'transform 100ms ease-in' },
      ]}
    >
      {backNavigationPath && (
        <div
          css={{
            position: 'fixed',
            top: 0,
            left: 0,
            bottom: 0,
            padding: spacing.xs,
            zIndex: 900,
          }}
          onTouchMove={handleDrag}
          onTouchEnd={handleDragEnd}
          onTouchStart={handleDragStart}
        />
      )}
      {snapshots &&
        backNavigationPath &&
        previousPageRoute &&
        dragStart.current &&
        snapshots[previousPageRoute] && (
          <img
            id="snapshot"
            css={[
              {
                position: 'fixed',
                inset: 0,
                zIndex: -1,
                width: '100%',
              },
              {
                transform: `translateX(-${dragNow}px)`,
                clipPath: `inset(0 ${window.innerWidth - dragNow}px 0 0)`,
              },
              dragNow === 0 && { transition: 'transform 100ms ease-in' },
            ]}
            src={snapshots[previousPageRoute]}
          />
        )}
      {!shouldRenderAnimated && <div ref={animatedPageRef}>{content}</div>}
      {shouldRenderAnimated && (
        <AnimatedPage ref={animatedPageRef} direction={navDirection}>
          <div>{content}</div>
        </AnimatedPage>
      )}
    </div>
  );
};

const useStyles = createStyles(({ spacing, breakpoints }) => ({
  wrapper: {
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  largeWrapper: {
    [breakpoints.laptopSmall]: {
      maxWidth: '100%',
    },
  },
  container: {
    flex: 1,
    margin: `0 ${spacing.xxs}`,
  },
  largeContainer: {
    [breakpoints.laptopSmall]: {
      margin: 0,
    },
  },
  buttonSeparator: {
    marginTop: spacing.xxs,
  },
  buttonContainer: {
    width: '100%',
    margin: `${spacing.xxs} 0`,
  },
  footer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    padding: `${spacing.xxxs} ${spacing.xxs}`,
    margin: 'auto',
    marginTop: spacing.xxxs,
    [breakpoints.laptopSmall]: {
      padding: `${spacing.s} ${spacing.xxs}`,
    },
  },
}));
